import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { DragonfruitPortal } from '../../portal';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';
export type TooltipAlignment = 'start' | 'center' | 'end';

const PADDING = 2;

function computePositioning(
  side: TooltipSide,
  alignment: TooltipAlignment,
  boundingBox: DOMRect,
  maxWidth: number,
): React.CSSProperties {
  let style: React.CSSProperties;
  if (side === 'top') {
    style = {
      top: boundingBox.top - PADDING,
      transform: 'translateY(-100%)',
    };
  } else if (side === 'bottom') {
    style = {
      top: boundingBox.top + boundingBox.height + PADDING,
      transform: '',
    };
  } else if (side === 'left') {
    style = {
      left: boundingBox.left - PADDING,
      transform: 'translateX(-100%)',
    };
  } else {
    style = {
      left: boundingBox.left + boundingBox.width + PADDING,
      transform: '',
    };
  }
  style.maxWidth = maxWidth;
  style.width = 'max-content';

  if (side === 'right') {
    style.justifyContent = 'start';
  } else if (side === 'left') {
    style.justifyContent = 'end';
  } else if (side === 'top' || side === 'bottom') {
    if (alignment === 'center') {
      style.justifyContent = 'center';
    } else if (alignment === 'end') {
      style.justifyContent = 'end';
    }
  }

  if (side === 'top' || side === 'bottom') {
    if (alignment === 'start') {
      style.left = boundingBox.left;
    } else if (alignment === 'center') {
      style.left = boundingBox.left + boundingBox.width / 2;
      style.transform += ' translateX(-50%)';
    } else {
      style.right = window.innerWidth - boundingBox.right;
    }
  } else if (side === 'left' || side === 'right') {
    if (alignment === 'start') {
      style.top = boundingBox.top;
    } else if (alignment === 'center') {
      style.top = boundingBox.top + boundingBox.height / 2;
      style.transform += ' translateY(-50%)';
    } else {
      style.bottom = window.innerHeight - boundingBox.bottom;
    }
  }

  return style;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** The part of the visual gap shared by the trigger and tooltip. */
function computeGapRect(side: TooltipSide, triggerRect: Rect, tooltipRect: Rect): Rect {
  if (side === 'top' || side === 'bottom') {
    const left = Math.max(triggerRect.left, tooltipRect.left);
    const width =
      Math.min(triggerRect.left + triggerRect.width, tooltipRect.left + tooltipRect.width) - left;
    if (side === 'top') {
      const top = tooltipRect.top + tooltipRect.height;
      return { top, left, width, height: triggerRect.top - top };
    }
    const top = triggerRect.top + triggerRect.height;
    return { top, left, width, height: tooltipRect.top - top };
  }

  const top = Math.max(triggerRect.top, tooltipRect.top);
  const height =
    Math.min(triggerRect.top + triggerRect.height, tooltipRect.top + tooltipRect.height) - top;
  if (side === 'left') {
    const left = tooltipRect.left + tooltipRect.width;
    return { top, left, width: triggerRect.left - left, height };
  }
  const left = triggerRect.left + triggerRect.width;
  return { top, left, width: tooltipRect.left - left, height };
}

function isPointInRect(x: number, y: number, rect: Rect): boolean {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    x >= rect.left &&
    x <= rect.left + rect.width &&
    y >= rect.top &&
    y <= rect.top + rect.height
  );
}

export interface InternalFixedTooltipPlacementProps {
  side: TooltipSide;
  alignment: TooltipAlignment;
  tooltip: JSX.Element;
  /**
   * Whether the content wrapper should fill its parent. This can be used in the case where e.g.
   * you are wrapping a button that you want to fill its parent.
   */
  shouldContentWrapperFillContainerWidth?: boolean;
  shouldContentWrapperFillContainerHeight?: boolean;
  /**
   * When undefined, the tooltip will show/hide on mouse enter/leave, respectively.
   * When false, the tooltip will not be shown, regardless of mouse positioning.
   * When true, the tooltip will always be shown, again regardless of mouse positioning.
   */
  overrideShow?: boolean;
  /** When defined, the tooltip will stay open if the mouse is over the tooltip. */
  interactive?: boolean;
  maxWidth: number;
  children?: React.ReactNode;
}

const InternalFixedTooltipPlacement: React.FC<InternalFixedTooltipPlacementProps> = (props) => {
  const {
    children,
    interactive,
    side,
    alignment,
    tooltip,
    shouldContentWrapperFillContainerWidth,
    shouldContentWrapperFillContainerHeight,
    overrideShow,
    maxWidth,
  } = props;

  const [mouseOver, setMouseOver] = useState<boolean>(false);

  const enableShowOnMouseOver = overrideShow === undefined;
  const showTooltip = overrideShow || (enableShowOnMouseOver && mouseOver);

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties | undefined>(undefined);

  // Add scroll event listener to hide tooltip when scrolling.
  useEffect(() => {
    if (!showTooltip) {
      return;
    }

    const handleScroll = (): void => {
      // Hide tooltip on scroll by resetting mouse state.
      setMouseOver(false);
    };

    document.addEventListener('scroll', handleScroll, true);

    return (): void => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [showTooltip]);

  useLayoutEffect(
    function updateTooltipStyle() {
      if (triggerRef.current === null || !showTooltip) {
        return;
      }
      const triggerRect = triggerRef.current.getBoundingClientRect();
      setStyle(computePositioning(side, alignment, triggerRect, maxWidth));
    },
    [alignment, maxWidth, side, showTooltip],
  );

  // The tooltip renders in a portal, so a document listener correlates its pointer trail with the
  // trigger and uses geometry only for the gap between them.
  useEffect(
    function watchPointerForDismissal() {
      if (!showTooltip || !interactive) {
        return;
      }
      const handlePointerMove = (event: MouseEvent): void => {
        const trigger = triggerRef.current;
        const tooltipBody = tooltipRef.current;
        if (trigger === null || tooltipBody === null) {
          setMouseOver(false);
          return;
        }
        const pointerTrail = event.composedPath();
        if (pointerTrail.includes(trigger) || pointerTrail.includes(tooltipBody)) {
          return;
        }
        const gapRect = computeGapRect(
          side,
          trigger.getBoundingClientRect(),
          tooltipBody.getBoundingClientRect(),
        );
        if (!isPointInRect(event.clientX, event.clientY, gapRect)) {
          setMouseOver(false);
        }
      };
      // Once the pointer leaves the viewport no further moves arrive, so the move handler alone
      // would leave the tooltip stuck open. `mouseleave` on the root element is the signal for
      // that: unlike `mouseout`, it fires only when the pointer truly crosses out of the element,
      // never when a node under the cursor is removed — which a continuously updating pane does
      // constantly, and which must not dismiss anything.
      const handleWindowExit = (): void => {
        setMouseOver(false);
      };
      document.addEventListener('mousemove', handlePointerMove);
      document.documentElement.addEventListener('mouseleave', handleWindowExit);
      return (): void => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.documentElement.removeEventListener('mouseleave', handleWindowExit);
      };
    },
    [showTooltip, interactive, side],
  );

  return (
    <div
      className={classNames('dragonfruit-tooltip-wrapper', {
        'should-fill-container-width': shouldContentWrapperFillContainerWidth,
        'should-fill-container-height': shouldContentWrapperFillContainerHeight,
      })}
      ref={triggerRef}
      onMouseEnter={(): void => {
        setMouseOver(true);
      }}
      onMouseLeave={(): void => {
        // The document listeners own dismissal for interactive tooltips: the pointer crossing
        // the gap to the tooltip body never reads as leaving the region.
        if (!interactive) {
          setMouseOver(false);
        }
      }}
    >
      {children}
      {showTooltip && (
        <DragonfruitPortal>
          <div
            ref={tooltipRef}
            className="dragonfruit-tooltip-placement fixed"
            data-testid="dragonfruit-tooltip-placement"
            role="tooltip"
            style={style}
            // It's possible to move your mouse in such a way that you hover the tooltip and it doesn't close,
            // so we have to do that manually.
            onMouseEnter={
              interactive
                ? undefined
                : (): void => {
                    setMouseOver(false);
                  }
            }
          >
            <div className="tooltip-wrapper">{tooltip}</div>
          </div>
        </DragonfruitPortal>
      )}
    </div>
  );
};

export default InternalFixedTooltipPlacement;
